import { resolveCurrencySymbol, resolveLoggedUserLocale } from "./currencyUtils";

export const TRAINING_EXPENSE_NOTE_PREFIX = "trexp";

export function trainingExpenseNoteForId(trainingId) {
  const id = String(trainingId || "").trim();
  if (!id || !/^[a-f\d]{24}$/i.test(id)) return "";
  return `${TRAINING_EXPENSE_NOTE_PREFIX}${id}`;
}

export function formatExpenseNotesDisplayFromTasksRow(expenseRow, trainingsEnvelope) {
  if (isTrainingExpenseEntry(expenseRow)) return "";
  const id = parseTrainingExpenseIdFromNotes(expenseRow?.notes ?? expenseRow?.Notes ?? "");
  if (!id) return expenseRow?.notes ?? expenseRow?.Notes ?? "";
  const list = unwrapTrainings(trainingsEnvelope);
  const t = list.find((x) => String(x?._id) === String(id));
  const trainingName = String(t?.trainingname || t?.trainingName || "").trim();
  const dueRaw = t?.duedate ?? t?.dueDate ?? null;
  const dueLabel =
    dueRaw &&
    (() => {
      try {
        const d = new Date(dueRaw);
        return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
      } catch {
        return "";
      }
    })();
  if (!trainingName) return dueLabel || expenseRow?.notes || expenseRow?.Notes || "";
  return [trainingName, dueLabel].filter(Boolean).join(" · ");
}

export function parseTrainingExpenseIdFromNotes(notes) {
  const n = String(notes || "").trim();
  if (!n.startsWith(TRAINING_EXPENSE_NOTE_PREFIX)) return "";
  const rest = n.slice(TRAINING_EXPENSE_NOTE_PREFIX.length);
  return /^[a-f\d]{24}$/i.test(rest) ? rest : "";
}

/** Training-linked other expense rows (persisted trexp-notes, synthetic sourceTrainingId, or explicit sourceType). */
export function isTrainingExpenseEntry(entry) {
  if (!entry || typeof entry !== "object") return false;
  if (entry.sourceType === "training") return true;
  if (String(entry.sourceTrainingId ?? entry.SourceTrainingId ?? "").trim()) return true;
  return Boolean(parseTrainingExpenseIdFromNotes(entry?.notes ?? entry?.Notes));
}

export function resolveTrainingApiRef(ref) {
  if (ref == null || ref === "") return "";
  if (typeof ref === "object") {
    const id = ref?._id ?? ref?.id;
    return id != null ? String(id).trim() : "";
  }
  return String(ref).trim();
}

export function unwrapTrainings(mytrainings) {
  if (!mytrainings) return [];
  if (Array.isArray(mytrainings?.trainings)) return mytrainings.trainings;
  if (Array.isArray(mytrainings)) return mytrainings;
  return [];
}

export function resolveTrainingAssignedDate(training, fallbackDate = null) {
  return (
    training?.assigneddate ??
    training?.assignedDate ??
    training?.trainingassigneddate ??
    training?.trainingAssignedDate ??
    training?.dateassigned ??
    training?.dateAssigned ??
    training?.createdAt ??
    training?.createdat ??
    training?.createdOn ??
    training?.startdate ??
    training?.startDate ??
    fallbackDate ??
    null
  );
}

export function resolveTrainingEndDate(training) {
  return (
    training?.enddate ??
    training?.endDate ??
    training?.completedAt ??
    training?.completedDate ??
    training?.completionDate ??
    training?.trainingCompletedAt ??
    null
  );
}

export function resolveTrainingAssigneeId(assignedRaw) {
  if (assignedRaw == null || assignedRaw === "") return "";
  if (typeof assignedRaw === "object") {
    const id = assignedRaw?._id ?? assignedRaw?.id;
    return id != null ? String(id).trim() : "";
  }
  return String(assignedRaw).trim();
}

/** L4 employee who can carry training cost as an OPS-style assignee (not PM-only accounts). */
export function isOperationsAssigneeEmployee(emp) {
  if (!emp) return false;
  const auth = String(emp?.EmpAuthLevel || "").trim().toUpperCase();
  const roles = ([]).concat(emp?.customRoles || []).map((r) => String(r || "").trim().toLowerCase());
  const isOpsLike = roles.some((r) => r.includes("operation"));
  const isPm = roles.some((r) => r === "project manager" || (r.includes("project") && r.includes("manager")));
  if (auth !== "L4") return false;
  if (isPm && !isOpsLike) return false;
  return isOpsLike || !isPm;
}

/** Hardware service assignees (e.g. Hardware Manager) — same My Trainings ↔ Other Expenses merge as L4 ops. */
export function isHardwareServiceAssigneeForTrainingExpense(emp) {
  if (!emp) return false;
  const st = String(emp?.servicetype ?? emp?.serviceType ?? "").trim().toLowerCase();
  if (st === "hardware" || st.includes("hardware")) return true;
  const roles = ([]).concat(emp?.customRoles || []).map((r) => String(r || "").trim().toLowerCase());
  if (roles.some((r) => r.includes("hardware") && r.includes("manager"))) return true;
  return false;
}

export function canBeAssigneeForMergedTrainingExpenseRows(emp) {
  return isOperationsAssigneeEmployee(emp) || isHardwareServiceAssigneeForTrainingExpense(emp);
}

/** Resolve ERP-ish project Id used by description tasks (`projectid`). */
export function resolveErpProjectIdForTraining(training, projectsList = []) {
  const direct = String(training?.projectid ?? training?.projectId ?? "").trim();
  if (direct) return direct;
  const quotationId = String(
    training?.quotationId ?? training?.quotationID ?? training?.quotationid ?? ""
  ).trim();
  if (quotationId) {
    const hit = projectsList.find(
      (p) => String(p?.quotationId ?? p?.quotationID ?? "").trim() === quotationId
    );
    if (hit?._id) return String(hit._id).trim();
  }
  const tName = String(training?.projectname ?? "").trim().toLowerCase();
  if (tName && projectsList.length) {
    const byName = projectsList.find((p) => String(p?.ProjectName ?? "").trim().toLowerCase() === tName);
    if (byName?._id) return String(byName._id).trim();
  }
  return "";
}

/** Project / lead linkage only (no assignee check). */
export function trainingMatchesProjectMeta(training, projectMeta) {
  if (!training) return false;

  const pid = resolveTrainingApiRef(training?.projectid ?? training?.projectId ?? training?.project);
  const qid = String(training?.quotationId ?? training?.quotationID ?? training?.quotationid ?? "").trim();
  const pname = String(training?.projectname ?? training?.ProjectName ?? "").trim().toLowerCase();
  const lid = String(training?.leadid ?? training?.leadId ?? "").trim();

  const candProjectIds = ([]).concat(
    projectMeta?.projectIds || [],
    projectMeta?.leadIds || [],
    projectMeta?.rowId ? [projectMeta.rowId] : [],
    projectMeta?.managerOpProjectId ? [projectMeta.managerOpProjectId] : []
  )
    .filter(Boolean)
    .map((x) => String(x).trim().toLowerCase());

  const candQuotationIds = ([]).concat(projectMeta?.quotationIds || []).filter(Boolean).map(String);
  const candNames = ([]).concat(projectMeta?.projectNames || []).filter(Boolean).map((x) =>
    String(x).trim().toLowerCase()
  );
  const candLeadIds = ([]).concat(projectMeta?.leadIds || []).filter(Boolean).map(String);

  const tp = pid ? String(pid).trim().toLowerCase() : "";
  if (tp && candProjectIds.includes(tp)) return true;

  const tq = qid.trim();
  if (tq && candQuotationIds.includes(tq)) return true;

  if (pname && candNames.some((n) => n && n === pname)) return true;

  const tlid = lid.trim();
  return Boolean(tlid && candLeadIds.includes(tlid));
}

/** Training matches an employee/project row (hardware manager roster). */
export function trainingMatchesEmployeeProject(training, assigneeEmpId, projectMeta) {
  if (!training || !assigneeEmpId) return false;

  const empIdStr = String(assigneeEmpId);
  const assignedRaw = training?.assignedto ?? training?.assignedTo;

  let assigneeMatches = resolveTrainingAssigneeId(assignedRaw) === empIdStr;
  if (!assigneeMatches && typeof assignedRaw === "string") {
    const raw = assignedRaw.trim().toLowerCase();
    const tEmail = raw.includes("@") ? raw : "";
    const empEmail = String(projectMeta?.assigneeEmail || "").trim().toLowerCase();
    assigneeMatches = Boolean(tEmail && empEmail && tEmail === empEmail);
  }
  if (!assigneeMatches) return false;

  return trainingMatchesProjectMeta(training, projectMeta);
}

/**
 * Project linkage fields for `trainingMatchesEmployeeProject` (assignee email added per person).
 */
export function buildProjectTrainingLinkMetaForRow(projectRow, projectsList = []) {
  if (!projectRow) {
    return {
      rowId: undefined,
      managerOpProjectId: undefined,
      projectIds: [],
      quotationIds: [],
      projectNames: [],
      leadIds: [],
    };
  }

  const leadLike = projectRow?.lead || projectRow?.Lead || {};
  const projectsArr = ([]).concat(projectsList || []).filter(Boolean);

  const projectIds = []
    .concat(
      projectRow?._id,
      projectRow?.projectid,
      projectRow?.projectId,
      projectRow?.ProjectId,
      leadLike?._id,
      leadLike?.id
    )
    .filter(Boolean)
    .map((x) => String(x).trim());

  const quotationKeys = []
    .concat(
      projectRow?.quotationId,
      projectRow?.quotationID,
      projectRow?.quotationid,
      projectRow?.QuotationId,
      projectRow?.QuotationID,
      leadLike?.quotationId,
      leadLike?.quotationID,
      leadLike?.quotationid
    )
    .filter(Boolean)
    .map((x) => String(x).trim());

  quotationKeys.forEach((qid) => {
    const hit = projectsArr.find(
      (p) => String(p?.quotationId ?? p?.quotationID ?? "").trim() === qid
    );
    if (hit?._id) projectIds.push(String(hit._id).trim());
  });

  const projectNames = []
    .concat(
      projectRow?.ProjectName,
      projectRow?.projectname,
      leadLike?.ProjectName,
      leadLike?.projectname
    )
    .filter(Boolean)
    .map((x) => String(x).trim().toLowerCase());

  const leadIds = []
    .concat(
      projectRow?.leadid,
      projectRow?.leadId,
      leadLike?.leadid,
      leadLike?.leadId
    )
    .filter(Boolean)
    .map((x) => String(x).trim());

  return {
    rowId: projectRow?._id,
    managerOpProjectId: undefined,
    projectIds,
    quotationIds: quotationKeys,
    projectNames,
    leadIds,
  };
}

function isMyTrainingStatusCompleted(training) {
  return String(training?.status ?? training?.Status ?? "").trim().toLowerCase() === "completed";
}

/**
 * Project Ongoing — "Submit Project" may proceed only when every My Trainings record
 * that is tied to this project and assigned to someone on `Assignedperson` has status Completed.
 * If none match, returns true.
 */
export function areAllAssignedTrainingsCompletedForOngoingProject({
  projectRow,
  trainingsEnvelope,
  employees = [],
  projects = [],
} = {}) {
  if (!projectRow) return true;

  const list = unwrapTrainings(trainingsEnvelope);
  if (!list.length) return true;

  const assignedPeople = ([]).concat(projectRow?.Assignedperson || []);
  if (!assignedPeople.length) return true;

  const scope = buildProjectTrainingLinkMetaForRow(projectRow, projects);
  const byTrainingId = new Map();
  const normEmail = (v) => String(v ?? "").trim().toLowerCase();

  const pnameKey = String(projectRow?.ProjectName ?? projectRow?.projectname ?? "")
    .trim()
    .toLowerCase();

  for (const person of assignedPeople) {
    const email = normEmail(person?.email ?? person?.EmpEmail);
    const emp =
      (employees || []).find((e) => normEmail(e?.EmpEmail) === email) ||
      (employees || []).find((e) => String(e?._id) === String(person?._id || person?.id));
    const assigneeId = emp?._id != null ? String(emp._id).trim() : "";
    if (!assigneeId) continue;

    const assigneeEmail = normEmail(emp?.EmpEmail || person?.email || person?.EmpEmail);
    const projectMeta = {
      ...scope,
      assigneeEmail,
    };

    for (const t of list) {
      let matches = trainingMatchesEmployeeProject(t, assigneeId, projectMeta);
      if (!matches && pnameKey) {
        const tName = String(t?.projectname || "").trim().toLowerCase();
        if (tName && tName === pnameKey) {
          const assignedRaw = t?.assignedto ?? t?.assignedTo;
          const idMatch = resolveTrainingAssigneeId(assignedRaw) === assigneeId;
          let emailMatch = false;
          if (typeof assignedRaw === "string") {
            const tEmail = assignedRaw.trim().toLowerCase();
            emailMatch = Boolean(tEmail && assigneeEmail && tEmail === assigneeEmail);
          }
          matches = idMatch || emailMatch;
        }
      }
      if (!matches) continue;
      const tid = String(t?._id || "").trim();
      if (tid) byTrainingId.set(tid, t);
    }
  }

  if (!byTrainingId.size) return true;
  return [...byTrainingId.values()].every(isMyTrainingStatusCompleted);
}

/**
 * Sum of My Trainings costs for an assignee on a project row (same linkage as Project Requested / manager assign).
 * Used to align Other Expenses "Training Cost" with My Trainings when persisted description tasks are stale or duplicated.
 */
export function getLiveTrainingRollupForOpsAssigneeProject({
  projectRow,
  assigneeEmp,
  trainingsEnvelope,
  projects = [],
  currencySymbol = "",
  managerOPProject = null,
} = {}) {
  if (!projectRow || !assigneeEmp) return null;

  const resolveTrainingRefId = (ref) => {
    if (ref == null || ref === "") return "";
    if (typeof ref === "object") {
      const id = ref?._id ?? ref?.id;
      return id != null ? String(id).trim() : "";
    }
    return String(ref).trim();
  };
  const normKey = (v) => String(v || "").trim().toLowerCase();

  const list = unwrapTrainings(trainingsEnvelope);
  const assigneeIdStr = String(assigneeEmp?._id || "").trim();
  const assigneeEmail = normKey(assigneeEmp?.EmpEmail ?? assigneeEmp?.email);
  if (!assigneeIdStr && !assigneeEmail) return null;

  const leadLike = projectRow?.lead || projectRow?.Lead || {};
  const opLeadLike = managerOPProject?.lead || managerOPProject?.Lead || {};
  const projectsList = ([]).concat(projects || []).filter(Boolean);

  const projectIds = []
    .concat(
      projectRow?._id,
      managerOPProject?._id,
      projectRow?.projectid,
      projectRow?.projectId,
      projectRow?.ProjectId,
      leadLike?._id,
      leadLike?.id,
      managerOPProject?.projectid,
      managerOPProject?.projectId
    )
    .filter(Boolean)
    .map((x) => String(x).trim());

  const quotationKeys = []
    .concat(
      projectRow?.quotationId,
      projectRow?.quotationID,
      projectRow?.quotationid,
      projectRow?.QuotationId,
      projectRow?.QuotationID,
      leadLike?.quotationId,
      leadLike?.quotationID,
      leadLike?.quotationid,
      managerOPProject?.quotationId,
      managerOPProject?.quotationID,
      managerOPProject?.quotationid,
      opLeadLike?.quotationId,
      opLeadLike?.quotationID
    )
    .filter(Boolean)
    .map((x) => String(x).trim());

  quotationKeys.forEach((qid) => {
    const hit = projectsList.find(
      (p) => String(p?.quotationId ?? p?.quotationID ?? "").trim() === qid
    );
    if (hit?._id) projectIds.push(String(hit._id).trim());
  });

  const quotationIds = quotationKeys;
  const projectNames = []
    .concat(
      projectRow?.ProjectName,
      projectRow?.projectname,
      leadLike?.ProjectName,
      leadLike?.projectname,
      managerOPProject?.ProjectName,
      managerOPProject?.projectname,
      opLeadLike?.ProjectName,
      opLeadLike?.projectname
    )
    .filter(Boolean)
    .map((x) => String(x).trim().toLowerCase());
  const leadIds = []
    .concat(
      projectRow?.leadid,
      projectRow?.leadId,
      leadLike?.leadid,
      leadLike?.leadId,
      managerOPProject?.leadid,
      managerOPProject?.leadId,
      opLeadLike?.leadid,
      opLeadLike?.leadId
    )
    .filter(Boolean)
    .map((x) => String(x).trim());

  const projectMeta = {
    assigneeEmail,
    rowId: projectRow?._id,
    managerOpProjectId: managerOPProject?._id,
    projectIds,
    quotationIds,
    projectNames,
    leadIds,
  };

  const matched = [];
  const seenIds = new Set();
  for (const t of list) {
    const tid = resolveTrainingRefId(t?._id);
    const assignedToRaw = t?.assignedto ?? t?.assignedTo;
    const assignedToId = resolveTrainingRefId(assignedToRaw);
    const assignedToEmail =
      typeof assignedToRaw === "string"
        ? String(assignedToRaw).trim().toLowerCase()
        : normKey(t?.assignedToEmail ?? "");
    const matchesAssignee =
      (assigneeIdStr && assignedToId && String(assignedToId) === String(assigneeIdStr)) ||
      (assigneeEmail && assignedToEmail && assignedToEmail === assigneeEmail);
    if (!matchesAssignee) continue;

    let projectOk =
      assigneeIdStr && trainingMatchesEmployeeProject(t, assigneeIdStr, projectMeta);
    if (!projectOk) continue;
    if (tid && seenIds.has(tid)) continue;
    if (tid) seenIds.add(tid);
    matched.push(t);
  }

  const normalized = matched
    .map((t) => {
      const amount = Number(String(t?.trainingcost ?? t?.trainingCost ?? 0).replace(/,/g, ""));
      const notes = t?.notes ?? t?.note ?? "";
      return {
        amount: Number.isFinite(amount) ? amount : 0,
        notes: String(notes || "").trim(),
      };
    })
    .filter((x) => x.amount > 0 || String(x.notes || "").trim() !== "");

  const total = normalized.reduce((sum, x) => sum + (Number(x.amount) || 0), 0);
  const joinedNotes = Array.from(new Set(normalized.map((x) => x.notes).filter(Boolean))).join("; ");
  if (total <= 0 && !joinedNotes) return null;

  const sym = String(currencySymbol || "").trim();
  return {
    description: sym ? `Training Cost (${sym})` : "Training Cost",
    amount: total,
    notes: joinedNotes,
  };
}

export function buildTrainingExpensePayloadRow(
  training,
  employeesList = [],
  projectsList = [],
  fallbackErpProjectId = ""
) {
  const id = training?._id ? String(training._id).trim() : "";
  if (!id) return null;

  const assigneeEmpId = resolveTrainingAssigneeId(training?.assignedto ?? training?.assignedTo);
  const assigneeEmp = employeesList.find((e) => String(e?._id) === String(assigneeEmpId));
  if (!canBeAssigneeForMergedTrainingExpenseRows(assigneeEmp)) return null;

  const projectId =
    resolveErpProjectIdForTraining(training, projectsList) ||
    String(fallbackErpProjectId ?? "").trim();
  if (!projectId) return null;

  const amt = Number(
    String(training?.trainingcost ?? training?.trainingCost ?? 0)
      .toString()
      .replace(/,/g, "")
  );
  if (!Number.isFinite(amt)) return null;

  const locale = resolveLoggedUserLocale(employeesList);
  const currency = resolveCurrencySymbol(locale) || "";

  const trainingName = String(training?.trainingname || training?.trainingName || "").trim() || "(Training)";
  const dueRaw = training?.duedate ?? training?.dueDate ?? null;
  const dueLabel = dueRaw
    ? (() => {
        try {
          const d = new Date(dueRaw);
          return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
        } catch {
          return "";
        }
      })()
    : "";
  const uiNotes = [trainingName || "", dueLabel].filter(Boolean).join(" · ");
  const descriptionPart = currency ? `Training Cost (${currency})` : "Training Cost";

  const matchedProject =
    projectsList.find((p) => String(p?._id) === String(projectId)) || {};

  let typeservice = matchedProject?.TypeService ?? "";
  if (Array.isArray(typeservice)) {
    typeservice = typeservice.find(Boolean) || "";
  }
  typeservice = String(typeservice || "").trim();

  /** Wire-safe shape — notes intentionally empty in UI/payload per product rules; linkage via sourceTrainingId. */
  const entryWire = {
    description: descriptionPart,
    value: amt,
    notes: "",
    typeservice,
    taskstatus: "submit",
    tripduration: matchedProject?.tripduration ?? "",
  };

  return {
    projectid: projectId,
    assigneeEmpId,
    typeservice,
    entryWire,
    uiNotes,
    sourceTrainingId: id,
    sourceEmployeeId: String(assigneeEmpId),
    sourceType: "training",
  };
}

/** Strip to fields the description-task endpoint typically persists. */
export function normalizeDescriptionTaskWireEntry(entry) {
  if (!entry || typeof entry !== "object") return null;
  const out = {};
  const desc = entry.description ?? entry.Description;
  const notes = entry.notes ?? entry.Notes;
  const typeservice = entry.typeservice ?? entry.TypeService ?? entry.typeService ?? "";
  const value = entry.value ?? entry.Value;
  const taskstatus = entry.taskstatus ?? entry.taskStatus ?? "submit";
  const tripdur = entry.tripduration ?? entry.tripDuration ?? "";
  if (entry._id != null && entry._id !== "") out._id = entry._id;
  out.description = desc ?? "";
  out.notes = notes ?? "";
  out.typeservice = typeservice ?? "";
  out.value = value ?? "";
  out.taskstatus = taskstatus;
  out.tripduration = tripdur ?? "";
  const stId = entry.sourceTrainingId ?? entry.SourceTrainingId;
  if (stId != null && String(stId).trim() !== "") out.sourceTrainingId = String(stId).trim();
  if (entry.sourceType === "training") out.sourceType = "training";
  return out;
}

/**
 * Ensures merged other-expenses payload includes persisted manual rows plus current training-derived rows,
 * keyed by synthetic note ref (no duplicates for same training id).
 *
 * `existingEntries`: rows about to be sent (may include persisted tasks with `_id`).
 */
export function mergeTrainingExpenseRowsInto(existingEntries = [], traineePayloadBundles = []) {
  const list = ([]).concat(existingEntries || []).filter(Boolean);
  const manualKept = list.filter((row) => !isTrainingExpenseDisplayRow(row));

  const appended = (traineePayloadBundles || [])
    .map((r) =>
      normalizeDescriptionTaskWireEntry({
        ...(r?.entryWire || {}),
        sourceType: r?.sourceType || "training",
        sourceTrainingId: r?.sourceTrainingId,
      })
    )
    .filter(Boolean);

  return [...manualKept, ...appended];
}

/** Bundled training rows merged into Hardware Manager \"Resources\" Other Expenses (Project Requests bucket). */
export function getTrainingExpenseBundlesForManagerResource({
  trainingsEnvelope,
  employees,
  projects,
  selectedEmail,
  opemail,
  row,
  managerOPProject,
}) {
  if (!row) return [];

  const assigneeEmail = String(selectedEmail || opemail || "").trim().toLowerCase();
  const assigneeEmp = (employees || []).find(
    (e) => String(e?.EmpEmail || "").trim().toLowerCase() === assigneeEmail
  );
  if (!assigneeEmp?._id || !canBeAssigneeForMergedTrainingExpenseRows(assigneeEmp)) return [];

  const list = unwrapTrainings(trainingsEnvelope);
  const rowLeadLike = row?.lead || row?.Lead || {};

  const projectIds = ([]).concat(row?._id, managerOPProject?._id).filter(Boolean);
  const quotationIds = []
    .concat(
      row?.quotationId,
      row?.quotationID,
      row?.QuotationId,
      managerOPProject?.quotationId,
      managerOPProject?.quotationID,
      rowLeadLike?.quotationId,
      rowLeadLike?.quotationID
    )
    .filter(Boolean);

  const projectNames = ([]).concat(row?.ProjectName, row?.projectname, managerOPProject?.ProjectName)
    .map((x) => String(x || "").trim())
    .filter(Boolean);

  const leadIds = []
    .concat(row?.leadid, row?.leadId, managerOPProject?.leadid, rowLeadLike?.leadid)
    .filter(Boolean);

  const projectMeta = {
    assigneeEmail,
    rowId: row?._id,
    managerOpProjectId: managerOPProject?._id,
    projectIds,
    quotationIds,
    projectNames,
    leadIds,
  };

  const pnameKey = String(
    row?.ProjectName ?? row?.projectname ?? managerOPProject?.ProjectName ?? ""
  )
    .trim()
    .toLowerCase();

  const assigneeOk = (t) => {
    const ar = t?.assignedto ?? t?.assignedTo;
    if (resolveTrainingAssigneeId(ar) === String(assigneeEmp._id)) return true;
    if (typeof ar === "string") {
      const tEmail = String(ar).trim().toLowerCase();
      return Boolean(tEmail && tEmail === assigneeEmail);
    }
    return false;
  };

  const matchedTrainings = list.filter((t) => {
    if (!assigneeOk(t)) return false;
    if (trainingMatchesEmployeeProject(t, assigneeEmp._id, projectMeta)) return true;
    if (!pnameKey) return false;
    const tpn = String(t?.projectname || "").trim().toLowerCase();
    if (tpn !== pnameKey) return false;
    const tq = String(t?.quotationId ?? t?.quotationID ?? t?.quotationid ?? "").trim();
    const tlid = String(t?.leadid ?? t?.leadId ?? "").trim();
    const tp = String(resolveTrainingApiRef(t?.projectid ?? t?.projectId ?? t?.project) ?? "")
      .trim()
      .toLowerCase();
    const candP = new Set(projectIds.map((x) => String(x).trim().toLowerCase()).filter(Boolean));
    const candQ = new Set(quotationIds.map((x) => String(x).trim()).filter(Boolean));
    const candL = new Set(leadIds.map((x) => String(x).trim()).filter(Boolean));
    return (
      (tq && candQ.has(tq)) ||
      (tlid && candL.has(tlid)) ||
      (tp && candP.has(tp))
    );
  });

  const fallbackPid = String(managerOPProject?._id || row?._id || "").trim();

  return matchedTrainings
    .map((t) => buildTrainingExpensePayloadRow(t, employees || [], projects || [], fallbackPid))
    .filter(Boolean)
    .filter((b) => String(b.assigneeEmpId) === String(assigneeEmp._id));
}

/** True when the manager assignment row is a Hardware service (OPS Other Expenses aggregation applies here only). */
export function assignmentRowIndicatesHardware(row) {
  const ts = row?.TypeService;
  if (ts === "Hardware") return true;
  if (Array.isArray(ts)) return ts.some((x) => String(x || "").trim() === "Hardware");
  return false;
}

/** Normalize Mongo-style ids for stable Set / equality checks. */
export function normalizeTaskIdForScope(id) {
  const s = String(id ?? "").trim();
  if (/^[a-f\d]{24}$/i.test(s)) return s.toLowerCase();
  return s;
}

/**
 * True when a description-task row belongs to this assignment row / linked OP project
 * (handles string vs ObjectId casing).
 */
export function taskProjectIdMatchesOpsManagerResourceScopes(task, row, managerOPProject) {
  const pid = normalizeTaskIdForScope(task?.projectid);
  if (!pid) return false;
  const scope = new Set(
    [row?._id, managerOPProject?._id]
      .filter(Boolean)
      .map((x) => normalizeTaskIdForScope(x))
  );
  const lead = row?.lead ?? row?.Lead;
  const leadId =
    lead && typeof lead === "object"
      ? lead._id ?? lead.id
      : lead;
  if (leadId != null && leadId !== "") {
    scope.add(normalizeTaskIdForScope(leadId));
  }
  return scope.has(pid);
}

/** True when the project/assignment row is an Installation service line. */
export function isInstallationAssignmentRow(row) {
  return normalizeAssignmentTypeServiceKey(row?.TypeService) === "installation";
}

/**
 * Project Ongoing → Other Expenses (manager + operation person): for Installation only,
 * show description-task rows that were saved with an Amount (`value`), matching Project Assign
 * validation. Hides empty template lines (e.g. Car Rental with no saved amount).
 */
export function filterInstallationOngoingOtherExpenseDisplayRows(rows = [], projectRow) {
  if (!isInstallationAssignmentRow(projectRow)) {
    return rows;
  }
  const list = Array.isArray(rows) ? rows : [];
  return list.filter((entry) => {
    if (!entry || typeof entry !== "object") return false;
    if (isTrainingExpenseDisplayRow(entry)) return true;
    if (entry._isTrainingCost || entry._prmSyntheticTrainingExpense) return true;
    const id = String(entry._id ?? "");
    if (
      id.startsWith("training_cost_") ||
      id.startsWith("prm-synthetic") ||
      id.startsWith("prm-training-rollup")
    ) {
      return true;
    }
    const raw = entry?.value ?? entry?.Value ?? "";
    if (raw == null || String(raw).trim() === "") return false;
    const n = Number(String(raw).replace(/,/g, ""));
    return Number.isFinite(n) && n > 0;
  });
}

/**
 * Project Requested — Installation manager / operations person:
 * autosave may persist template Other Expense lines with empty Amount; Submit must omit them.
 */
export function resolveInstallationOtherExpenseRowsForPersistence(
  rows = [],
  projectRow,
  { forSubmit = false } = {}
) {
  if (!isInstallationAssignmentRow(projectRow)) {
    return Array.isArray(rows) ? rows : [];
  }
  if (forSubmit) {
    return filterInstallationOngoingOtherExpenseDisplayRows(rows, projectRow);
  }
  const list = Array.isArray(rows) ? rows : [];
  return list.filter((entry) => {
    if (!entry || typeof entry !== "object") return false;
    return !isTrainingExpenseDisplayRow(entry);
  });
}

/** Normalize TypeService to a single keyword: cloud, installation, hardware, software. */
export function normalizeAssignmentTypeServiceKey(typeService) {
  const raw = Array.isArray(typeService)
    ? typeService.find(Boolean)
    : typeService;
  const s = String(raw ?? "").trim().toLowerCase();
  if (!s) return "";
  if (s.includes("installation")) return "installation";
  if (s.includes("hardware")) return "hardware";
  if (s.includes("cloud")) return "cloud";
  if (s.includes("software")) return "software";
  return s;
}

/** Standard Installation Other Expenses template line (Airfare, Lodging, etc.). */
export function isInstallationOtherExpenseTemplateDescription(rawDesc) {
  const d = normalizeDescriptionForTrainingCostMatch(rawDesc);
  if (!d) return false;
  if (d.includes("miscellaneous install")) return true;
  if (d.includes("airfare")) return true;
  if (d.includes("car rental")) return true;
  if (d.includes("car fuel")) return true;
  if (d.includes("lodging")) return true;
  return false;
}

const NON_INSTALLATION_OTHER_EXPENSE_ROW_KEYS = new Set([
  "cloud",
  "software",
  "hardware",
]);

/**
 * Description-task `typeservice` must match the assignment row (Cloud vs Installation on same lead).
 */
export function taskTypeServiceMatchesAssignmentRow(task, row, managerOPProject = null) {
  const rowKey = normalizeAssignmentTypeServiceKey(row?.TypeService);
  if (!rowKey) return true;

  const taskKey = normalizeAssignmentTypeServiceKey(
    task?.typeservice ?? task?.TypeService ?? task?.typeService
  );
  // Hide only when the row is explicitly tagged Installation on a non-installation service line.
  // Do not hide by description label (e.g. "Airfare") — Software/Cloud/Hardware may use those lines.
  if (taskKey === "installation" && NON_INSTALLATION_OTHER_EXPENSE_ROW_KEYS.has(rowKey)) {
    return false;
  }
  if (!taskKey) {
    const pid = normalizeTaskIdForScope(task?.projectid);
    const rowPid = normalizeTaskIdForScope(row?._id);
    const opPid = normalizeTaskIdForScope(managerOPProject?._id);
    return pid === rowPid || Boolean(opPid && pid === opPid);
  }
  return taskKey === rowKey;
}

/**
 * OPS resource Other Expenses in Project Requests: same project scope + matching service line.
 */
export function taskMatchesOpsManagerResourceOtherExpenses(task, row, managerOPProject) {
  if (!taskProjectIdMatchesOpsManagerResourceScopes(task, row, managerOPProject)) {
    return false;
  }
  return taskTypeServiceMatchesAssignmentRow(task, row, managerOPProject);
}

/** Normalize description for stable "training cost" matching (NBSP, ZWSP, double spaces, etc.). */
export function normalizeDescriptionForTrainingCostMatch(raw) {
  return String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/\ufeff/g, "")
    .replace(/[\s\u00a0\u1680\u2000-\u200b\u202f\u205f\u3000]+/g, " ")
    .trim();
}

/**
 * True when the label looks like a persisted "Training Cost (₹)" / "Training  Cost" / "TrainingCost" line item.
 * Kept separate from {@link isTrainingExpenseEntry} so plain persisted rows still consolidate.
 */
export function descriptionLooksLikeTrainingCostLine(rawDesc) {
  const d = normalizeDescriptionForTrainingCostMatch(rawDesc);
  if (!d) return false;
  if (d.includes("training cost")) return true;
  const compact = d.replace(/[\s_-]/g, "");
  return compact.includes("trainingcost");
}

export function isTrainingExpenseDisplayRow(task) {
  if (!task || typeof task !== "object") return false;
  if (isTrainingExpenseEntry(task)) return true;
  const raw = task?.description ?? task?.Description ?? "";
  return descriptionLooksLikeTrainingCostLine(raw);
}

/** OP/manager ongoing: only explicit saved `expenses`, not planned Amount (`value`). */
export function parseSavedOngoingExpenseRaw(item) {
  if (!item) return null;
  const raw = item?.expenses ?? item?.Expenses ?? item?.expense;
  if (raw == null || String(raw).trim() === "") return null;
  const n = Number(String(raw).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

export function getOngoingExpenseNumberForTracker(item, draftValue) {
  if (draftValue != null && String(draftValue).trim() !== "") {
    const n = Number(String(draftValue).replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
  }
  if (isTrainingExpenseDisplayRow(item)) {
    return Number(item?.expenses ?? item?.value ?? 0) || 0;
  }
  return parseSavedOngoingExpenseRaw(item) ?? 0;
}

export function formatOngoingExpenseInputValue(item, draftValue, commaFormat) {
  if (draftValue != null && String(draftValue).trim() !== "") return draftValue;
  const saved = parseSavedOngoingExpenseRaw(item);
  if (saved == null) return "";
  return commaFormat(saved);
}

/**
 * Collapse persisted description-task training rows to the My Trainings total
 * (avoids duplicate project scopes or stale amounts vs My Trainings).
 */
export function applyLiveTrainingTotalToOtherExpenseRows(rows, liveTotal, liveNotes, currencySymbol) {
  const n = Number(liveTotal);
  if (!Number.isFinite(n) || n <= 0 || !Array.isArray(rows)) return rows;
  const manuals = rows.filter((r) => !isTrainingExpenseDisplayRow(r));
  const trains = rows.filter((r) => isTrainingExpenseDisplayRow(r));
  if (!trains.length) return rows;
  const template = trains[0];
  const sym = String(currencySymbol || "").trim();
  const desc =
    String(template?.description ?? template?.Description ?? "").trim() ||
    (sym ? `Training Cost (${sym})` : "Training Cost");
  const merged = {
    ...template,
    description: desc,
    value: n,
    notes: String(liveNotes ?? template?.notes ?? template?.Notes ?? "").trim(),
  };
  if (template?.expenses != null && template?.expenses !== "") merged.expenses = n;
  return [...manuals, merged];
}

/**
 * Collapse "Training cost" / "Training Cost (₹)" / etc. to one row label in
 * Installation Associated Costs (Actual + Quoted finance tables).
 */
export function canonicalInstallationAssociatedTaskDescription(desc, currencySymbol = "") {
  const raw = desc == null ? "" : String(desc).trim();
  if (!raw) return "";
  if (isTrainingExpenseDisplayRow({ description: raw })) {
    const sym = String(currencySymbol || "").trim();
    return sym ? `Training Cost (${sym})` : "Training Cost";
  }
  return raw;
}

/** Normalize installation other-expense description for matching API rows to template rows. */
export function normalizeInstallationExpenseDescriptionKey(desc) {
  return String(desc ?? "")
    .trim()
    .toLowerCase()
    .replace(/\ufeff/g, "")
    .replace(/[\s\u00a0\u2000-\u200b]+/g, " ")
    .trim();
}

/** Default Project Requests installation other-expense line items (before user fill). */
export function getDefaultInstallationOtherExpenseRows(currencySymbol = "") {
  const sym = String(currencySymbol || "").trim();
  const suf = sym ? ` (${sym})` : "";
  return [
    { description: `Airfare${suf}`, value: "", notes: "" },
    { description: `Car Rental${suf}`, value: "", notes: "" },
    { description: `Car fuel${suf}`, value: "", notes: "" },
    { description: `Lodging${suf}`, value: "", notes: "" },
    { description: `Miscellaneous Install Items to buy${suf}`, value: "", notes: "" },
  ];
}

/**
 * Merge persisted description-task rows with the standard installation template.
 * When the API returns only training rows (or omits standard lines), Airfare / Lodging / etc. stay visible.
 */
export function mergeInstallationOtherExpenseRowsWithDefaults(matchedTasks = [], currencySymbol = "") {
  const defaults = getDefaultInstallationOtherExpenseRows(currencySymbol);

  const list = ([]).concat(matchedTasks || []).filter(Boolean);
  const trainingRows = list.filter((t) => isTrainingExpenseDisplayRow(t));
  const nonTraining = list.filter((t) => !isTrainingExpenseDisplayRow(t));

  // No persisted manual rows: show full template (training-only or empty bucket).
  if (nonTraining.length === 0) {
    return [...defaults, ...trainingRows];
  }

  // Only include default template rows that exist on the server; do not pad lines the user removed.
  const mergedDefaults = [];
  for (const def of defaults) {
    const k = normalizeInstallationExpenseDescriptionKey(def.description);
    const hit = nonTraining.find(
      (t) => normalizeInstallationExpenseDescriptionKey(t.description) === k
    );
    if (hit) {
      mergedDefaults.push({ ...def, ...hit, description: def.description });
    }
  }

  const usedKeys = new Set(
    mergedDefaults.map((r) => normalizeInstallationExpenseDescriptionKey(r.description))
  );
  const extras = [];
  for (const t of nonTraining) {
    const k = normalizeInstallationExpenseDescriptionKey(t.description);
    if (usedKeys.has(k)) continue;
    extras.push({ ...t });
    usedKeys.add(k);
  }

  return [...mergedDefaults, ...extras, ...trainingRows];
}

/**
 * P&L tables merge My Trainings "quoted" rows into `tasks` (`SalesFinaceFilter` → `isSyntheticTrainingCost`).
 * Those must not add on top of SP / description-task training rows already stored per person.
 */
export function isFinancePlSyntheticTrainingInjectionRow(entry) {
  if (!entry || typeof entry !== "object") return false;
  if (entry.isSyntheticTrainingCost === true) return true;
  const id = String(entry._id ?? "");
  if (id.startsWith("training-cost-")) return true;
  return false;
}

/** True for description-task rows persisted from API (not P&L injection, not UI merge synthetics). */
export function isPersistedInstallationTrainingApiTask(entry) {
  if (!entry || typeof entry !== "object") return false;
  if (!isTrainingExpenseDisplayRow(entry)) return false;
  if (isFinancePlSyntheticTrainingInjectionRow(entry)) return false;
  if (entry._prmSyntheticTrainingExpense === true) return false;
  if (entry._prmAggregatedTrainingExpense === true) return false;
  if (entry._prmHwAggregatedTrainingExpense === true) return false;
  const id = String(entry._id ?? "");
  if (id.startsWith("prm-")) return false;
  return true;
}

/**
 * Pick the installation project row used to resolve My Trainings ↔ project linkage
 * for a column's description-task entries.
 */
export function resolveInstallationProjectRowForTrainingRollup(entries = [], matchedProjects = []) {
  const list = ([]).concat(matchedProjects || []).filter(Boolean);
  const installationProjects = list.filter((p) => {
    const ts = p?.TypeService;
    if (!ts) return false;
    if (Array.isArray(ts)) {
      return ts.some((x) => String(x || "").toLowerCase().includes("installation"));
    }
    return String(ts).toLowerCase().includes("installation");
  });
  const pickFrom = installationProjects.length ? installationProjects : list;
  const idSet = new Set(
    ([]).concat(entries || []).map((e) => String(e?.projectid ?? "").trim()).filter(Boolean)
  );
  if (idSet.size) {
    const hit = pickFrom.find((p) => idSet.has(String(p?._id ?? "").trim()));
    if (hit) return hit;
  }
  return pickFrom[0] || null;
}

/**
 * Quoted installation associated costs: training cell uses My Trainings rollup when available
 * (same source as Response Received), else sums description tasks.
 */
export function getInstallationQuotedTrainingDisplayAmount({
  entries = [],
  matchedProjects = [],
  assigneeEmp,
  trainingsEnvelope,
  projects = [],
  currencySymbol = "",
}) {
  const projectRow = resolveInstallationProjectRowForTrainingRollup(entries, matchedProjects);
  if (projectRow && assigneeEmp) {
    const live = getLiveTrainingRollupForOpsAssigneeProject({
      projectRow,
      assigneeEmp,
      trainingsEnvelope,
      projects,
      currencySymbol,
    });
    const liveAmt = Number(live?.amount ?? 0);
    if (Number.isFinite(liveAmt) && liveAmt > 0) return liveAmt;
  }
  return sumInstallationAssociatedTrainingValues(entries);
}

/**
 * Actual installation associated costs: same rollup preference using training cost amounts.
 */
export function getInstallationActualTrainingDisplayAmount({
  entries = [],
  matchedProjects = [],
  assigneeEmp,
  trainingsEnvelope,
  projects = [],
  currencySymbol = "",
}) {
  const projectRow = resolveInstallationProjectRowForTrainingRollup(entries, matchedProjects);
  if (projectRow && assigneeEmp) {
    const live = getLiveTrainingRollupForOpsAssigneeProject({
      projectRow,
      assigneeEmp,
      trainingsEnvelope,
      projects,
      currencySymbol,
    });
    const liveAmt = Number(live?.amount ?? 0);
    if (Number.isFinite(liveAmt) && liveAmt > 0) return liveAmt;
  }
  return sumInstallationAssociatedTrainingExpenses(entries);
}

function installationTrainingRows(entries = []) {
  return ([]).concat(entries || []).filter((e) =>
    isTrainingExpenseDisplayRow({ description: e?.description ?? e?.Description })
  );
}

/** True when this person's column has at least one API-persisted training description-task row. */
export function hasPersistedInstallationTrainingExpenseRows(entries = []) {
  return installationTrainingRows(entries).some((e) => isPersistedInstallationTrainingApiTask(e));
}

/**
 * For chart / flat aggregates: omit My-Training injections when SP description tasks
 * already include training for that person (avoids double-count in pie + totals).
 */
export function filterInstallationAssociatedExpenseEntriesForAggregation(entries = []) {
  const list = ([]).concat(entries || []).filter(Boolean);
  if (!hasPersistedInstallationTrainingExpenseRows(list)) return list;
  return list.filter((e) => {
    if (isFinancePlSyntheticTrainingInjectionRow(e) && isTrainingExpenseDisplayRow(e)) return false;
    return true;
  });
}

/** Sum amounts for all training-type rows (Installation associated table — `expenses` field). */
export function sumInstallationAssociatedTrainingExpenses(entries = []) {
  const trains = installationTrainingRows(entries);
  const persisted = trains.filter((e) => isPersistedInstallationTrainingApiTask(e));
  if (persisted.length) {
    return persisted.reduce((sum, e) => {
      const n = Number(String(e?.expenses ?? e?.value ?? 0).replace(/,/g, ""));
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }
  const fallback = trains.filter((e) => !isFinancePlSyntheticTrainingInjectionRow(e));
  const source = fallback.length ? fallback : trains;
  return source.reduce((sum, e) => {
    const n = Number(String(e?.expenses ?? e?.value ?? 0).replace(/,/g, ""));
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
}

/** Sum amounts for all training-type rows when source uses `value` (quoted installation path). */
export function sumInstallationAssociatedTrainingValues(entries = []) {
  const trains = installationTrainingRows(entries);
  const persisted = trains.filter((e) => isPersistedInstallationTrainingApiTask(e));
  if (persisted.length) {
    return persisted.reduce((sum, e) => {
      const n = Number(String(e?.value ?? e?.expenses ?? 0).replace(/,/g, ""));
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }
  const fallback = trains.filter((e) => !isFinancePlSyntheticTrainingInjectionRow(e));
  const source = fallback.length ? fallback : trains;
  return source.reduce((sum, e) => {
    const n = Number(String(e?.value ?? e?.expenses ?? 0).replace(/,/g, ""));
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
}

function hardwareOtherExpenseTrainingTaskRow(task) {
  return isTrainingExpenseDisplayRow(task);
}

function descriptionTaskNumericValue(task) {
  const n = Number(String(task?.value ?? task?.Value ?? task?.amount ?? task?.Amount ?? task?.expenses ?? 0).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function parseTrainingDateMillis(d) {
  if (d == null || d === "") return null;
  const dt = d instanceof Date ? d : new Date(d);
  const t = dt.getTime();
  return Number.isNaN(t) ? null : t;
}

function pickAggregatedTrainingDateFromRows(rows, getRaw, mode) {
  let bestVal = null;
  let bestMs = null;
  for (const x of rows) {
    const raw = getRaw(x);
    if (raw == null || raw === "") continue;
    const ms = parseTrainingDateMillis(raw);
    if (ms == null) continue;
    if (
      bestMs == null ||
      (mode === "max" && ms > bestMs) ||
      (mode === "min" && ms < bestMs)
    ) {
      bestMs = ms;
      bestVal = raw;
    }
  }
  return bestVal;
}

/**
 * When My Trainings has multiple rows for the same project, merge their
 * earliest start and latest end onto displayed training expense rows
 * (e.g. Project Ongoing persisted rows often omit end dates).
 */
export function applyMyTrainingsDateBoundsToTrainingRows(rows, bounds) {
  if (!Array.isArray(rows) || !bounds || typeof bounds !== "object") {
    return rows || [];
  }
  const rawEnd = bounds.enddate ?? bounds.endDate;
  const rawStart = bounds.startdate ?? bounds.startDate;
  const endMs = parseTrainingDateMillis(rawEnd);
  const startMs = parseTrainingDateMillis(rawStart);
  if (endMs == null && startMs == null) return rows;

  return rows.map((r) => {
    if (!isTrainingExpenseDisplayRow(r)) return r;
    const next = { ...r };
    if (endMs != null) {
      const curMs = parseTrainingDateMillis(r.enddate ?? r.endDate);
      if (curMs == null || endMs > curMs) {
        next.enddate = rawEnd;
      }
    }
    if (startMs != null) {
      const curMs = parseTrainingDateMillis(r.startdate ?? r.startDate);
      if (curMs == null || startMs < curMs) {
        next.startdate = rawStart;
      }
    }
    return next;
  });
}

export function consolidateTrainingExpenseRowsForDisplay(tasks = []) {
  const list = ([]).concat(tasks || []).filter(Boolean);
  const manuals = [];
  const trains = [];
  list.forEach((r) => {
    (isTrainingExpenseDisplayRow(r) ? trains : manuals).push(r);
  });
  if (trains.length <= 1) return list;

  const [template] = trains;
  const owner = String(template?.taskownerid ?? "").trim();
  const proj = String(template?.projectid ?? "").trim();
  const sum = trains.reduce((s, x) => s + descriptionTaskNumericValue(x), 0);
  const startdate =
    pickAggregatedTrainingDateFromRows(
      trains,
      (x) => x?.startdate ?? x?.startDate,
      "min"
    ) ??
    template?.startdate ??
    template?.startDate;
  const enddate =
    pickAggregatedTrainingDateFromRows(
      trains,
      (x) => x?.enddate ?? x?.endDate,
      "max"
    ) ??
    template?.enddate ??
    template?.endDate;
  const notes = Array.from(
    new Set(
      trains
        .map((x) => String(x?.notes ?? x?.Notes ?? "").trim())
        .filter((n) => n && !n.startsWith(TRAINING_EXPENSE_NOTE_PREFIX))
    )
  ).join("; ");
  const merged = {
    ...template,
    _id: `prm-synthetic-training-expense-total-${proj || "project"}-${owner || "owner"}`,
    taskownerid: template?.taskownerid,
    projectid: template?.projectid,
    description:
      String(template?.description ?? template?.Description ?? "").trim() || "Training Cost",
    value: sum,
    notes,
    startdate,
    enddate,
    sourceTrainingId: `training-total-${proj || "project"}-${owner || "owner"}`,
    sourceType: "training",
    readOnly: true,
    isLocked: true,
    _prmSyntheticTrainingExpense: true,
    _prmAggregatedTrainingExpense: true,
  };

  if ("amount" in template && !("value" in template)) {
    delete merged.value;
    merged.amount = sum;
  }

  return [...manuals, merged];
}

/**
 * Merge multiple persisted/synthetic training expense tasks for the same assignee + project into ONE row (summed amount).
 * Used for Hardware manager → viewing an operations assignee → Other Expenses only.
 */
export function consolidateHardwareTrainingExpenseTasksForMergedView(tasks = []) {
  const list = ([]).concat(tasks || []).filter(Boolean);
  const manuals = [];
  const trains = [];
  list.forEach((r) => {
    (hardwareOtherExpenseTrainingTaskRow(r) ? trains : manuals).push(r);
  });
  if (trains.length <= 1) return list;

  const byKey = new Map();
  trains.forEach((t) => {
    const owner = normalizeTaskIdForScope(t?.taskownerid);
    const proj = normalizeTaskIdForScope(t?.projectid);
    const k = `${owner}::${proj}`;
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(t);
  });

  const mergedTrains = [];
  byKey.forEach((grp) => {
    if (grp.length === 1) {
      mergedTrains.push(grp[0]);
      return;
    }
    const [template] = grp;
    const owner = normalizeTaskIdForScope(template?.taskownerid);
    const proj = normalizeTaskIdForScope(template?.projectid);
    const sum = grp.reduce((s, x) => s + descriptionTaskNumericValue(x), 0);
    mergedTrains.push({
      ...template,
      _id: `prm-synthetic-training-expense-hwagg-${proj}-${owner}`,
      taskownerid: owner,
      projectid: proj,
      description:
        String(template?.description ?? template?.Description ?? "").trim() || "Training Cost",
      value: sum,
      notes: "",
      sourceTrainingId: `hw-training-total-${owner}`,
      sourceType: "training",
      readOnly: true,
      _prmSyntheticTrainingExpense: true,
      _prmHwAggregatedTrainingExpense: true,
    });
  });

  return [...manuals, ...mergedTrains];
}

/**
 * Merge every "Training Cost*" description-task row for the same assignee + project
 * (within the given project ids) into one row with summed amount. Safe on the global
 * description-task list because only matching assignee + scope + training rows are touched.
 */
export function mergeAssigneeScopeTrainingCostRowsInDescriptionTasks(
  allTasks = [],
  assigneeEmpId,
  scopeProjectIds = [],
  extraTrainingProjectIds = []
) {
  const list = ([]).concat(allTasks || []).filter(Boolean);
  const oid = normalizeTaskIdForScope(assigneeEmpId);
  if (!oid) return list;

  const scopes = new Set(
    ([]).concat(scopeProjectIds || [], extraTrainingProjectIds || []).map(normalizeTaskIdForScope).filter(Boolean)
  );
  if (!scopes.size) return list;

  const trainingSlice = [];
  const rest = [];
  for (const t of list) {
    const pid = normalizeTaskIdForScope(t?.projectid);
    const owner = normalizeTaskIdForScope(t?.taskownerid);
    const inScope =
      owner === oid &&
      isTrainingExpenseDisplayRow(t) &&
      (scopes.has(pid) || pid === "");
    if (inScope) trainingSlice.push(t);
    else rest.push(t);
  }
  if (trainingSlice.length <= 1) return list;

  const sum = trainingSlice.reduce((s, x) => s + descriptionTaskNumericValue(x), 0);
  const template = trainingSlice[0];
  const canonicalPid =
    [...scopes].find((s) => trainingSlice.some((x) => normalizeTaskIdForScope(x?.projectid) === s)) ||
    normalizeTaskIdForScope(template?.projectid) ||
    [...scopes][0] ||
    "";

  const merged = {
    ...template,
    _id: `prm-training-rollup-${oid}-${canonicalPid || "project"}`,
    taskownerid: template?.taskownerid ?? assigneeEmpId,
    projectid: canonicalPid || template?.projectid,
    description:
      String(template?.description ?? template?.Description ?? "").trim() || "Training Cost",
    value: sum,
    notes: "",
    sourceTrainingId: `hw-training-total-${oid}`,
    sourceType: "training",
    readOnly: true,
    _prmSyntheticTrainingExpense: true,
    _prmAggregatedTrainingExpense: true,
  };
  if ("amount" in template && !("value" in template)) {
    merged.amount = sum;
    delete merged.value;
  }
  return [...rest, merged];
}

/**
 * Collapse several training payload bundles (same assignee) into a single bundle whose amount is the sum.
 * Keeps one wire row for replaceAll / UI.
 */
export function collapseTrainingBundlesToSingleRowPerAssignee(bundles = []) {
  const list = ([]).concat(bundles || []).filter(Boolean);
  if (list.length <= 1) return list;

  let total = 0;
  list.forEach((b) => {
    const raw = Number(String(b?.entryWire?.value ?? 0).replace(/,/g, ""));
    if (Number.isFinite(raw)) total += raw;
  });
  if (total <= 0) return [];

  const first = list[0];
  const oid = String(first?.assigneeEmpId || "").trim();
  if (!oid) return list;

  const ew = first.entryWire || {};
  return [
    {
      ...first,
      assigneeEmpId: oid,
      sourceTrainingId: `hw-training-total-${oid}`,
      sourceType: "training",
      entryWire: {
        ...ew,
        value: total,
        notes: "",
      },
    },
  ];
}

function descriptionTaskHasTrainingExpenseForScope(tasksRows, trainingId, projectId, taskOwnerId) {
  const tStr = String(trainingId || "").trim();
  const pStr = projectId != null && projectId !== "" ? normalizeTaskIdForScope(projectId) : "";
  const oStr = taskOwnerId != null && taskOwnerId !== "" ? normalizeTaskIdForScope(taskOwnerId) : "";
  return ([]).concat(tasksRows || []).some((r) => {
    const nid = parseTrainingExpenseIdFromNotes(r?.notes ?? r?.Notes ?? "");
    const matchesNotesId = nid && String(nid) === tStr;
    const matchesFieldId = String(r?.sourceTrainingId ?? "").trim() === tStr;
    if (!matchesNotesId && !matchesFieldId) return false;
    if (pStr && normalizeTaskIdForScope(r?.projectid) !== pStr) return false;
    if (oStr && normalizeTaskIdForScope(r?.taskownerid) !== oStr) return false;
    return true;
  });
}

/**
 * Drop scoped "Training Cost" description tasks for an assignee and replace with one row
 * whose amount matches My Trainings (fixes duplicate persisted rows, e.g. 500+500 showing 1000).
 */
export function reconcileDescriptionTasksTrainingCostWithLiveRollup(
  tasks = [],
  {
    row,
    managerOPProject = null,
    assigneeEmp = null,
    trainingsEnvelope,
    employees = [],
    projects = [],
    scopeProjectIds = [],
  } = {}
) {
  if (!Array.isArray(tasks) || !tasks.length) return tasks;
  if (!row || !assigneeEmp?._id) return tasks;
  if (!canBeAssigneeForMergedTrainingExpenseRows(assigneeEmp)) return tasks;

  const locale = resolveLoggedUserLocale(employees);
  const currencySymbol = resolveCurrencySymbol(locale) || "";

  const live = getLiveTrainingRollupForOpsAssigneeProject({
    projectRow: row,
    managerOPProject,
    assigneeEmp,
    trainingsEnvelope,
    projects,
    currencySymbol,
  });
  if (!live) return tasks;

  const liveAmt = Number(live.amount);
  if (!Number.isFinite(liveAmt)) return tasks;

  const oid = normalizeTaskIdForScope(assigneeEmp._id);
  const scopes = new Set(
    ([]).concat(scopeProjectIds, row?._id, managerOPProject?._id)
      .map((x) => normalizeTaskIdForScope(x))
      .filter(Boolean)
  );
  if (!scopes.size) return tasks;

  let hadScopedTraining = false;
  const rest = [];
  let template = null;
  for (const t of tasks) {
    const own = normalizeTaskIdForScope(t?.taskownerid);
    const pid = normalizeTaskIdForScope(t?.projectid);
    if (own === oid && scopes.has(pid) && isTrainingExpenseDisplayRow(t)) {
      hadScopedTraining = true;
      if (!template) template = { ...t };
      continue;
    }
    rest.push(t);
  }

  if (liveAmt <= 0) {
    return hadScopedTraining ? rest : tasks;
  }

  const hardwareRow = assignmentRowIndicatesHardware(row);
  const canonicalPid =
    hardwareRow && managerOPProject?._id
      ? normalizeTaskIdForScope(managerOPProject._id)
      : normalizeTaskIdForScope(row._id) || [...scopes][0];

  const desc =
    String(live.description || "").trim() ||
    (currencySymbol ? `Training Cost (${currencySymbol})` : "Training Cost");

  let typeservice = row?.TypeService ?? "";
  if (Array.isArray(typeservice)) typeservice = typeservice.find(Boolean) || "";
  typeservice = String(typeservice || "").trim();

  const base = template || {};
  const merged = {
    ...base,
    _id: `prm-live-training-rollup-${oid}-${canonicalPid || "p"}`,
    projectid: canonicalPid,
    taskownerid: assigneeEmp._id,
    tasktype: base.tasktype || "other",
    description: desc,
    value: liveAmt,
    notes: String(live.notes ?? "").trim(),
    typeservice: base.typeservice || typeservice,
    taskstatus: base.taskstatus || "submit",
    tripduration: base.tripduration ?? row?.tripduration ?? "",
    sourceType: "training",
    sourceTrainingId: `live-rollup-${oid}-${canonicalPid || "p"}`,
    readOnly: true,
    _prmSyntheticTrainingExpense: true,
    _prmLiveRollupTrainingExpense: true,
  };

  return [...rest, merged];
}

/**
 * HARD DATA FIX: append synthetic description-task rows (same shape Airfare rows use —
 * description / value / notes / projectid / taskownerid) so Redux `tasks` fed into
 * ManagerTaskInputTable / TaskInputTable / Hardware picks up Training Cost immediately.
 *
 * Emits duplicates for distinct `projectid` scopes (request row `_id` vs `managerOPProject._id`)
 * because Other Expenses UIs filter tasks by one OR the other.
 *
 * For Hardware assignments: multiple trainings for the same OPS assignee are collapsed to ONE summed
 * Training Cost row; synthetic rows use the manager OP project id when available (matches OperationHardware filter).
 */
export function mergeDescriptionTasksWithTrainingExpenseRows(rawTasks = [], ctx = {}) {
  const base = ([]).concat(rawTasks || []).filter(Boolean);
  const {
    row,
    managerOPProject,
    trainingsEnvelope,
    employees = [],
    projects = [],
    selectedemail,
    selectedEmail,
    opemail,
    enableDebugLogs = false,
  } = ctx;

  if (!row?._id) return base;

  const assigneePick = selectedemail ?? selectedEmail;
  const assigneeEmail = String(assigneePick || opemail || "").trim().toLowerCase();
  const assigneeEmp = ([]).concat(employees || []).find(
    (e) => String(e?.EmpEmail || "").trim().toLowerCase() === assigneeEmail
  );

  const trainings = unwrapTrainings(trainingsEnvelope);

  const projectsList = ([]).concat(projects || []).filter(Boolean);

  const scopeIds = ([]).concat(row?._id, managerOPProject?._id).filter(Boolean);
  const uniqueScopeIds = [...new Set(scopeIds.map((id) => String(id).trim()).filter(Boolean))];

  const hardwareRow = assignmentRowIndicatesHardware(row);

  /** Mirror for STEP 7: same field names as persisted other-expense description tasks (`value`). */
  const mirrorOtherExpenseRowsForAssigneeAndScopes = (rows) => {
    if (!assigneeEmp?._id || !uniqueScopeIds.length) return [];
    const oid = String(assigneeEmp._id);
    return ([]).concat(rows || []).filter((t) =>
      uniqueScopeIds.some((pid) => String(t?.projectid ?? "") === pid) &&
      String(t?.taskownerid ?? "") === oid
    );
  };

  const bundlesRaw = getTrainingExpenseBundlesForManagerResource({
    trainingsEnvelope,
    employees,
    projects: projectsList,
    selectedEmail: selectedemail ?? selectedEmail,
    opemail,
    row,
    managerOPProject,
  });

  const bundles = collapseTrainingBundlesToSingleRowPerAssignee(bundlesRaw);

  const bundleProjectIds = bundlesRaw
    .map((b) => String(b?.projectid ?? "").trim())
    .filter(Boolean);

  const synthScopeIds =
    hardwareRow && managerOPProject?._id
      ? [String(managerOPProject._id).trim()].filter(Boolean)
      : uniqueScopeIds;

  const assigneeOidForStrip = assigneeEmp?._id ? normalizeTaskIdForScope(assigneeEmp._id) : "";
  const trainingProjectIdsFromBase = assigneeOidForStrip
    ? base
        .filter(
          (t) =>
            normalizeTaskIdForScope(t?.taskownerid) === assigneeOidForStrip &&
            isTrainingExpenseDisplayRow(t)
        )
        .map((t) => normalizeTaskIdForScope(t?.projectid))
        .filter(Boolean)
    : [];

  const stripPidSet = new Set(
    [...uniqueScopeIds, ...bundleProjectIds, ...synthScopeIds, ...trainingProjectIdsFromBase]
      .map((x) => normalizeTaskIdForScope(x))
      .filter(Boolean)
  );

  let out = [...base];

  /** Drop stale per-training rows for this assignee (all service types) before re-injecting merged training. */
  if (assigneeEmp?._id && bundles.length > 0 && stripPidSet.size > 0) {
    const oidStrip = normalizeTaskIdForScope(assigneeEmp._id);
    out = out.filter((t) => {
      if (!hardwareOtherExpenseTrainingTaskRow(t)) return true;
      if (normalizeTaskIdForScope(t?.taskownerid) !== oidStrip) return true;
      const tp = normalizeTaskIdForScope(t?.projectid);
      return !stripPidSet.has(tp);
    });
  }

  if (hardwareRow) {
    out = consolidateHardwareTrainingExpenseTasksForMergedView(out);
  }

  for (const b of bundles) {
    const tid = String(b?.sourceTrainingId || "").trim();
    const oid = String(b?.assigneeEmpId || "").trim();
    const ew = b?.entryWire;
    if (!tid || !oid || !ew) continue;

    for (const pid of synthScopeIds) {
      if (!pid) continue;
      if (descriptionTaskHasTrainingExpenseForScope(out, tid, pid, oid)) continue;
      out.push({
        _id: `prm-synthetic-training-expense-${tid}-${pid}-${oid}`,
        projectid: pid,
        taskownerid: oid,
        tasktype: "other",
        description: ew.description ?? "",
        value: ew.value ?? "",
        notes: "",
        typeservice: ew.typeservice ?? "",
        taskstatus: ew.taskstatus ?? "submit",
        tripduration: ew.tripduration ?? "",
        sourceType: "training",
        sourceTrainingId: tid,
        readOnly: true,
        _prmSyntheticTrainingExpense: true,
      });
    }
  }

  if (enableDebugLogs && typeof console !== "undefined" && console.log) {
    // eslint-disable-next-line no-console
    console.log("ROW DATA", row);
    // eslint-disable-next-line no-console
    console.log("TRAININGS", trainings);
    // eslint-disable-next-line no-console
    console.log(
      "OTHER EXPENSES",
      mirrorOtherExpenseRowsForAssigneeAndScopes(out)
    );
  }

  const assigneeOidMerge = assigneeEmp?._id ? normalizeTaskIdForScope(assigneeEmp._id) : "";
  const trainingProjectIdsFromOut = assigneeOidMerge
    ? out
        .filter(
          (t) =>
            normalizeTaskIdForScope(t?.taskownerid) === assigneeOidMerge &&
            isTrainingExpenseDisplayRow(t)
        )
        .map((t) => normalizeTaskIdForScope(t?.projectid))
        .filter(Boolean)
    : [];

  const mergeScopeIds = [
    ...new Set(
      []
        .concat(uniqueScopeIds, bundleProjectIds, trainingProjectIdsFromOut)
        .map(normalizeTaskIdForScope)
        .filter(Boolean)
    ),
  ];

  if (assigneeEmp?._id && mergeScopeIds.length) {
    out = mergeAssigneeScopeTrainingCostRowsInDescriptionTasks(
      out,
      String(assigneeEmp._id),
      mergeScopeIds,
      []
    );
  }

  if (assigneeEmp?._id && canBeAssigneeForMergedTrainingExpenseRows(assigneeEmp)) {
    const scopeForLive = mergeScopeIds.length ? mergeScopeIds : uniqueScopeIds;
    out = reconcileDescriptionTasksTrainingCostWithLiveRollup(out, {
      row,
      managerOPProject,
      assigneeEmp,
      trainingsEnvelope,
      employees,
      projects: projectsList,
      scopeProjectIds: scopeForLive,
    });
  }

  return out;
}
